import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "inovacao", label: "I · Inovação em Licitações" },
  { id: "sustentabilidade", label: "II · Sustentabilidade nas Contratações" },
  { id: "gestao", label: "III · Gestão e Resultados" },
];

const QUESTIONS = [
  { id: "q_case", label: "1. Descreva o case inscrito. O que foi implementado pelo seu órgão?" },
  { id: "q_problem", label: "2. Qual problema o órgão enfrentava antes do case?" },
  { id: "q_results", label: "3. Quais resultados concretos foram alcançados? (números, prazos, economia)" },
  { id: "q_differential", label: "4. O que diferencia o case das práticas tradicionais?" },
  { id: "q_replication", label: "5. O case pode ser replicado por outros órgãos? Como?" },
];

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  role: "",
  organization: "",
  city: "",
  state: "",
  category: "inovacao",
  q_case: "",
  q_problem: "",
  q_results: "",
  q_differential: "",
  q_replication: "",
  accepted_rules: false,
};

const fieldCls = "bg-[#2A1708]/60 border-[#D9A441]/25 text-[#F6EBD7] placeholder:text-[#E6D7BC]/30 focus-visible:ring-[#D9A441]/40";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-display text-xs font-semibold uppercase tracking-[0.14em] text-[#D9A441]">{label}</Label>
    {children}
  </div>
);

export default function PremioModal({ open, onOpenChange }) {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.trim().includes("@") &&
    form.phone.trim().length >= 8 &&
    form.role.trim().length >= 2 &&
    form.organization.trim().length >= 2 &&
    form.city.trim().length >= 2 &&
    form.state.trim().length >= 2 &&
    QUESTIONS.every((q) => form[q.id].trim().length >= 10) &&
    form.accepted_rules;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/premio/inscricao`, { ...form, state: form.state.toUpperCase() });
      setDone(true);
      toast.success("Case inscrito no Prêmio CEAP BRASIL!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setSending(false); }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-2xl text-[#F6EBD7] max-h-[92vh] overflow-y-auto"
        style={{ background: "linear-gradient(170deg, #241209 0%, #160B05 100%)", border: "1px solid rgba(217,164,65,0.3)" }}
        data-testid="premio-modal"
        data-lenis-prevent
      >
        {done ? (
          <div className="py-10 text-center" data-testid="premio-success">
            <CheckCircle2 size={56} className="text-[#D9A441] mx-auto mb-5" />
            <DialogTitle className="font-serif text-2xl sm:text-3xl text-[#F6EBD7]">Case inscrito com sucesso!</DialogTitle>
            <DialogDescription className="mt-4 text-[#E6D7BC]/70 max-w-md mx-auto leading-relaxed">
              Recebemos a inscrição do seu case no Prêmio CEAP BRASIL de Excelência em Licitações. Os finalistas serão
              comunicados pelos canais oficiais e os vencedores serão premiados no palco no dia 04 de março de 2027.
            </DialogDescription>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 pt-2">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl sm:text-3xl text-[#F6EBD7]">
                Inscreva seu case
              </DialogTitle>
              <DialogDescription className="text-[#E6D7BC]/60 text-sm">
                Prêmio CEAP BRASIL de Excelência em Licitações · inscrições até 20/01/2027
              </DialogDescription>
            </DialogHeader>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome completo">
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} required className={fieldCls} data-testid="premio-name" />
              </Field>
              <Field label="E-mail">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="voce@orgao.gov.br" required className={fieldCls} data-testid="premio-email" />
              </Field>
              <Field label="Telefone WhatsApp">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(41) 99999-0000" required className={fieldCls} data-testid="premio-phone" />
              </Field>
              <Field label="Função">
                <Input value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="Ex: Pregoeira oficial" required className={fieldCls} data-testid="premio-role" />
              </Field>
              <Field label="Órgão">
                <Input value={form.organization} onChange={(e) => update("organization", e.target.value)} placeholder="Ex: Prefeitura de..." required className={fieldCls} data-testid="premio-organization" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Field label="Município">
                    <Input value={form.city} onChange={(e) => update("city", e.target.value)} required className={fieldCls} data-testid="premio-city" />
                  </Field>
                </div>
                <Field label="UF">
                  <Input value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} required className={`${fieldCls} uppercase`} data-testid="premio-state" />
                </Field>
              </div>
            </div>

            <Field label="Categoria do case">
              <div className="grid sm:grid-cols-3 gap-2.5" data-testid="premio-category-group">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => update("category", c.id)}
                    className={`rounded-xl px-3.5 py-3 text-left text-xs sm:text-sm font-semibold transition-all duration-300 border ${
                      form.category === c.id
                        ? "border-[#D9A441] bg-[#D9A441]/15 text-[#F6EBD7]"
                        : "border-[#D9A441]/20 bg-[#2A1708]/40 text-[#E6D7BC]/60 hover:border-[#D9A441]/50"
                    }`}
                    data-testid={`premio-cat-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </Field>

            {QUESTIONS.map((q) => (
              <Field key={q.id} label={q.label}>
                <Textarea
                  value={form[q.id]}
                  onChange={(e) => update(q.id, e.target.value)}
                  rows={3}
                  required
                  className={`${fieldCls} resize-none`}
                  data-testid={`premio-${q.id}`}
                />
              </Field>
            ))}

            <label className="flex items-start gap-3 rounded-xl border border-[#D9A441]/25 bg-[#2A1708]/40 p-4 cursor-pointer" data-testid="premio-accept-wrap">
              <input
                type="checkbox"
                checked={form.accepted_rules}
                onChange={(e) => update("accepted_rules", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#D9A441]"
                data-testid="premio-accept"
              />
              <span className="text-xs sm:text-sm text-[#E6D7BC]/75 leading-relaxed">
                Li e aceito integralmente as regras do{" "}
                <a href="/edital-premio-ceap-2027.pdf" target="_blank" rel="noopener noreferrer" className="text-[#E8C37A] underline underline-offset-2">
                  edital do prêmio
                </a>
                , incluindo critérios de avaliação, prazos e autorização de divulgação do case.
              </span>
            </label>

            <button
              type="submit"
              disabled={!canSubmit || sending}
              className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full font-display font-bold uppercase tracking-[0.06em] text-sm text-[#241209] transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none hover:-translate-y-0.5"
              style={{ background: "linear-gradient(92deg, #E8C37A 0%, #D9A441 100%)", boxShadow: "0 14px 40px rgba(217,164,65,0.3)" }}
              data-testid="premio-submit"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Enviando...
                </>
              ) : (
                "Enviar inscrição do case"
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
